import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.tsx';
import { msalConfig } from './auth/msal.ts';
import { ThemeProvider } from './theme/context.tsx';
import { I18nProvider } from './i18n/context.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';

const pca = new PublicClientApplication(msalConfig);

pca.addEventCallback((ev) => {
  if (ev.eventType === EventType.LOGIN_SUCCESS && ev.payload) {
    const accounts = pca.getAllAccounts();
    if (accounts.length > 0 && !pca.getActiveAccount()) {
      pca.setActiveAccount(accounts[0]!);
    }
  }
  if (ev.eventType === EventType.LOGIN_FAILURE || ev.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
    console.error('[msal]', ev.eventType, ev.error);
  }
});

await pca.initialize();

// Finish the auth-code redirect before React mounts so the hash is consumed
// even if <MsalProvider>'s own call races with StrictMode double-invoke.
try {
  const result = await pca.handleRedirectPromise();
  if (result?.account) pca.setActiveAccount(result.account);
} catch (err) {
  console.error('[msal] redirect handling failed:', err);
}

const accounts = pca.getAllAccounts();
if (accounts.length > 0 && !pca.getActiveAccount()) {
  pca.setActiveAccount(accounts[0]!);
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: 1 } },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <I18nProvider>
        <ErrorBoundary>
          <MsalProvider instance={pca}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </QueryClientProvider>
          </MsalProvider>
        </ErrorBoundary>
      </I18nProvider>
    </ThemeProvider>
  </React.StrictMode>
);
